import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiPlus, FiEdit, FiTrash2, FiEye, FiEyeOff, FiSave, FiX, FiCalendar, FiUser, FiTag, FiImage, FiUpload, FiFileText, FiCode } from 'react-icons/fi';
import { useDropzone } from 'react-dropzone';
import ReactMarkdown from 'react-markdown';
import { getBlogs, createBlog, updateBlog, deleteBlog, uploadImage } from '../../services/api';

const Container = styled.div`
  padding: 80px 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin: 0;
`;

const AddButton = styled.button`
  background: ${props => props.theme.colors.secondary};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${props => props.theme.colors.secondaryDark};
    transform: translateY(-1px);
  }
`;

const BlogsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BlogCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    border-color: ${props => props.theme.colors.primary}20;
  }
`;

const BlogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const BlogTitle = styled.h3`
  font-size: 1.3rem;
  color: ${props => props.theme.colors.primary};
  margin: 0;
  flex: 1;
  margin-right: 1rem;
`;

const BlogStatus = styled.div`
  background: ${props => props.isPublished ? '#4CAF50' : '#FF9800'};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const BlogMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textSecondary};
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const BlogExcerpt = styled.p`
  color: ${props => props.theme.colors.text};
  line-height: 1.6;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BlogActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  background: ${props => props.variant === 'danger' ? '#f44336' : props.variant === 'success' ? '#4CAF50' : props.theme.colors.primary};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    transform: translateY(-1px);
    opacity: 0.9;
  }
`;

// Modal Styles
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const ModalTitle = styled.h2`
  color: ${props => props.theme.colors.primary};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${props => props.theme.colors.textLight};
  padding: 0.5rem;
  border-radius: 50%;
  transition: background 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.divider};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;



const RichTextArea = styled.div`
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  overflow: hidden;
`;

const Toolbar = styled.div`
  background: ${props => props.theme.colors.background};
  padding: 0.5rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ToolbarButton = styled.button`
  background: ${props => props.active ? props.theme.colors.primary : 'white'};
  color: ${props => props.active ? 'white' : props.theme.colors.text};
  border: 1px solid ${props => props.theme.colors.border};
  padding: 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    background: ${props => props.active ? props.theme.colors.primaryDark : props.theme.colors.background};
  }
`;

const ContentEditable = styled.div`
  padding: 1rem;
  min-height: 300px;
  max-height: 500px;
  overflow-y: auto;
  outline: none;
  line-height: 1.6;

  &:focus {
    background: #fafafa;
  }
`;

const TagsInput = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem;
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  min-height: 50px;
`;

const Tag = styled.span`
  background: ${props => props.theme.colors.primary}20;
  color: ${props => props.theme.colors.primary};
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RemoveTag = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  font-size: 1rem;
  padding: 0;
  display: flex;
  align-items: center;
`;

const TagInput = styled.input`
  border: none;
  outline: none;
  flex: 1;
  min-width: 100px;
  font-size: 0.9rem;
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const ImageUploadArea = styled.div`
  border: 2px dashed ${props => props.isDragActive ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.isDragActive ? `${props.theme.colors.primary}10` : 'white'};

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.primary}10;
  }
`;

const UploadIcon = styled.div`
  font-size: 2rem;
  color: ${props => props.theme.colors.textLight};
  margin-bottom: 1rem;
`;

const UploadText = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
`;

const ImagePreview = styled.div`
  margin-top: 1rem;
  
  img {
    max-width: 100%;
    max-height: 200px;
    border-radius: 8px;
    border: 2px solid ${props => props.theme.colors.border};
  }
`;

const ContentTypeToggle = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const ToggleButton = styled.button`
  background: ${props => props.active ? props.theme.colors.primary : 'white'};
  color: ${props => props.active ? 'white' : props.theme.colors.text};
  border: 1px solid ${props => props.theme.colors.border};
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;

  &:hover {
    background: ${props => props.active ? props.theme.colors.primaryDark : props.theme.colors.background};
  }
`;

const MarkdownPreview = styled.div`
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  padding: 1rem;
  background: #f8f9fa;
  max-height: 400px;
  overflow-y: auto;
  line-height: 1.6;

  h1, h2, h3, h4, h5, h6 {
    color: ${props => props.theme.colors.primary};
    margin-top: 1.5rem;
    margin-bottom: 0.5rem;
  }

  p {
    margin-bottom: 1rem;
  }

  ul, ol {
    margin-bottom: 1rem;
    padding-left: 1.5rem;
  }

  code {
    background: #e9ecef;
    padding: 0.2rem 0.4rem;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
  }

  pre {
    background: #f8f9fa;
    padding: 1rem;
    border-radius: 6px;
    overflow-x: auto;
    margin: 1rem 0;
  }

  blockquote {
    border-left: 4px solid ${props => props.theme.colors.primary};
    padding-left: 1rem;
    margin: 1rem 0;
    font-style: italic;
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.3s ease;
  font-family: ${props => props.contentType === 'markdown' ? "'Courier New', monospace" : 'inherit'};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const AdminBlogsPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    contentType: 'html',
    author: 'RPL Support Team',
    featuredImage: '',
    category: 'General',
    tags: [],
    isPublished: false,
    metaTitle: '',
    metaDescription: '',
    seoKeywords: []
  });
  const [newTag, setNewTag] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showMarkdownPreview, setShowMarkdownPreview] = useState(false);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await getBlogs();
      setBlogs(response.blogs || response);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Submitting blog data:', formData);
      if (editingBlog) {
        const result = await updateBlog(editingBlog._id, formData);
        console.log('Blog updated:', result);
      } else {
        const result = await createBlog(formData);
        console.log('Blog created:', result);
      }
      setShowModal(false);
      setEditingBlog(null);
      resetForm();
      fetchBlogs();
    } catch (error) {
      console.error('Error saving blog:', error);
      if (error.response?.data?.errors) {
        alert('Error saving blog: ' + JSON.stringify(error.response.data.errors));
      } else {
        alert('Error saving blog: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title,
      excerpt: blog.excerpt,
      content: blog.content,
      contentType: blog.contentType || 'html',
      author: blog.author,
      featuredImage: blog.featuredImage || '',
      category: blog.category,
      tags: blog.tags || [],
      isPublished: blog.isPublished,
      metaTitle: blog.metaTitle || '',
      metaDescription: blog.metaDescription || '',
      seoKeywords: blog.seoKeywords || []
    });
    setShowModal(true);
  };

  const handleDelete = async (blogId) => {
    if (window.confirm('Are you sure you want to delete this blog?')) {
      try {
        await deleteBlog(blogId);
        fetchBlogs();
      } catch (error) {
        console.error('Error deleting blog:', error);
      }
    }
  };

  const handleTogglePublish = async (blog) => {
    try {
      const updatedBlog = await updateBlog(blog._id, {
        ...blog,
        isPublished: !blog.isPublished
      });
      console.log('Blog updated:', updatedBlog);
      fetchBlogs();
    } catch (error) {
      console.error('Error updating blog:', error);
      alert('Error updating blog: ' + (error.response?.data?.message || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      contentType: 'html',
      author: 'RPL Support Team',
      featuredImage: '',
      category: 'General',
      tags: [],
      isPublished: false,
      metaTitle: '',
      metaDescription: '',
      seoKeywords: []
    });
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const addKeyword = () => {
    if (newKeyword.trim() && !formData.seoKeywords.includes(newKeyword.trim())) {
      setFormData({
        ...formData,
        seoKeywords: [...formData.seoKeywords, newKeyword.trim()]
      });
      setNewKeyword('');
    }
  };

  const removeKeyword = (keywordToRemove) => {
    setFormData({
      ...formData,
      seoKeywords: formData.seoKeywords.filter(keyword => keyword !== keywordToRemove)
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Image upload functionality
  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    console.log('Uploading file:', acceptedFiles[0]);
    setUploadingImage(true);
    try {
      console.log('Sending upload request...');
      const data = await uploadImage(acceptedFiles[0]);
      console.log('Upload response data:', data);

      if (data.success) {
        setFormData(prev => ({
          ...prev,
          featuredImage: data.imageUrl
        }));
        console.log('Image uploaded successfully:', data.imageUrl);
      } else {
        console.error('Upload failed:', data.message);
        alert('Failed to upload image: ' + data.message);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading image: ' + error.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    multiple: false,
    maxSize: 5 * 1024 * 1024 // 5MB
  });

  if (loading) {
    return (
      <Container>
        <LoadingContainer>Loading blogs...</LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Manage Blogs</Title>
        <AddButton onClick={() => setShowModal(true)}>
          <FiPlus />
          New Blog Post
        </AddButton>
      </Header>

      <BlogsGrid>
        {blogs.map((blog) => (
          <BlogCard key={blog._id}>
            <BlogHeader>
              <BlogTitle>{blog.title}</BlogTitle>
              <BlogStatus isPublished={blog.isPublished}>
                {blog.isPublished ? 'Published' : 'Draft'}
              </BlogStatus>
            </BlogHeader>
            
            <BlogMeta>
              <MetaItem>
                <FiUser />
                {blog.author}
              </MetaItem>
              <MetaItem>
                <FiCalendar />
                {formatDate(blog.createdAt)}
              </MetaItem>
              {blog.views > 0 && (
                <MetaItem>
                  <FiEye />
                  {blog.views} views
                </MetaItem>
              )}
            </BlogMeta>
            
            <BlogExcerpt>{blog.excerpt}</BlogExcerpt>
            
            <BlogActions>
              <ActionButton onClick={() => handleEdit(blog)}>
                <FiEdit />
                Edit
              </ActionButton>
              <ActionButton 
                variant="success" 
                onClick={() => handleTogglePublish(blog)}
              >
                {blog.isPublished ? <FiEyeOff /> : <FiEye />}
                {blog.isPublished ? 'Unpublish' : 'Publish'}
              </ActionButton>
              <ActionButton 
                variant="danger" 
                onClick={() => handleDelete(blog._id)}
              >
                <FiTrash2 />
                Delete
              </ActionButton>
            </BlogActions>
          </BlogCard>
        ))}
      </BlogsGrid>

      {showModal && (
        <Modal onClick={() => setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}
              </ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}>
                <FiX />
              </CloseButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Title *</Label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Enter blog title"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Excerpt *</Label>
                <TextArea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                  placeholder="Brief description of the blog post"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Content *</Label>
                <ContentTypeToggle>
                  <ToggleButton
                    type="button"
                    active={formData.contentType === 'html'}
                    onClick={() => setFormData({...formData, contentType: 'html'})}
                  >
                    <FiFileText />
                    HTML Editor
                  </ToggleButton>
                  <ToggleButton
                    type="button"
                    active={formData.contentType === 'markdown'}
                    onClick={() => setFormData({...formData, contentType: 'markdown'})}
                  >
                    <FiCode />
                    Markdown
                  </ToggleButton>
                  {formData.contentType === 'markdown' && (
                    <ToggleButton
                      type="button"
                      active={showMarkdownPreview}
                      onClick={() => setShowMarkdownPreview(!showMarkdownPreview)}
                    >
                      <FiEye />
                      {showMarkdownPreview ? 'Hide Preview' : 'Show Preview'}
                    </ToggleButton>
                  )}
                </ContentTypeToggle>

                {formData.contentType === 'html' ? (
                  <RichTextArea>
                    <Toolbar>
                      <ToolbarButton type="button" onClick={() => document.execCommand('bold')}>
                        <strong>B</strong>
                      </ToolbarButton>
                      <ToolbarButton type="button" onClick={() => document.execCommand('italic')}>
                        <em>I</em>
                      </ToolbarButton>
                      <ToolbarButton type="button" onClick={() => document.execCommand('underline')}>
                        <u>U</u>
                      </ToolbarButton>
                      <ToolbarButton type="button" onClick={() => document.execCommand('insertUnorderedList')}>
                        • List
                      </ToolbarButton>
                      <ToolbarButton type="button" onClick={() => document.execCommand('insertOrderedList')}>
                        1. List
                      </ToolbarButton>
                    </Toolbar>
                    <ContentEditable
                      contentEditable
                      dangerouslySetInnerHTML={{ __html: formData.content }}
                      onInput={(e) => setFormData({...formData, content: e.target.innerHTML})}
                      placeholder="Write your blog content here..."
                    />
                  </RichTextArea>
                ) : (
                  <div>
                    <TextArea
                      contentType={formData.contentType}
                      value={formData.content}
                      onChange={(e) => setFormData({...formData, content: e.target.value})}
                      placeholder="Write your blog content in Markdown format..."
                      style={{ minHeight: '300px' }}
                    />
                    {showMarkdownPreview && formData.content && (
                      <div style={{ marginTop: '1rem' }}>
                        <Label>Preview:</Label>
                        <MarkdownPreview>
                          <ReactMarkdown>{formData.content}</ReactMarkdown>
                        </MarkdownPreview>
                      </div>
                    )}
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Author</Label>
                <Input
                  type="text"
                  value={formData.author}
                  onChange={(e) => setFormData({...formData, author: e.target.value})}
                  placeholder="Author name"
                />
              </FormGroup>

              <FormGroup>
                <Label>Featured Image</Label>
                <ImageUploadArea {...getRootProps()} isDragActive={isDragActive}>
                  <input {...getInputProps()} />
                  <UploadIcon>
                    <FiUpload />
                  </UploadIcon>
                  <UploadText>
                    {uploadingImage ? 'Uploading...' : 
                     isDragActive ? 'Drop the image here' : 
                     'Drag & drop an image here, or click to select'}
                  </UploadText>
                  <UploadText style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>
                    Supports: JPG, PNG, GIF, WebP (max 5MB)
                  </UploadText>
                </ImageUploadArea>
                
                {formData.featuredImage && (
                  <ImagePreview>
                    <img src={formData.featuredImage} alt="Featured" />
                    <ActionButton 
                      type="button" 
                      variant="danger" 
                      onClick={() => setFormData({...formData, featuredImage: ''})}
                      style={{ marginTop: '0.5rem' }}
                    >
                      <FiX />
                      Remove Image
                    </ActionButton>
                  </ImagePreview>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Category</Label>
                <Input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  placeholder="Blog category"
                />
              </FormGroup>

              <FormGroup>
                <Label>Tags</Label>
                <TagsInput>
                  {formData.tags.map((tag, index) => (
                    <Tag key={index}>
                      {tag}
                      <RemoveTag onClick={() => removeTag(tag)}>
                        <FiX />
                      </RemoveTag>
                    </Tag>
                  ))}
                  <TagInput
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Add tag..."
                  />
                </TagsInput>
              </FormGroup>

              <FormGroup>
                <Label>SEO Keywords</Label>
                <TagsInput>
                  {formData.seoKeywords.map((keyword, index) => (
                    <Tag key={index}>
                      {keyword}
                      <RemoveTag onClick={() => removeKeyword(keyword)}>
                        <FiX />
                      </RemoveTag>
                    </Tag>
                  ))}
                  <TagInput
                    type="text"
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                    placeholder="Add keyword..."
                  />
                </TagsInput>
              </FormGroup>

              <FormGroup>
                <Label>Meta Title</Label>
                <Input
                  type="text"
                  value={formData.metaTitle}
                  onChange={(e) => setFormData({...formData, metaTitle: e.target.value})}
                  placeholder="SEO meta title"
                />
              </FormGroup>

              <FormGroup>
                <Label>Meta Description</Label>
                <TextArea
                  value={formData.metaDescription}
                  onChange={(e) => setFormData({...formData, metaDescription: e.target.value})}
                  placeholder="SEO meta description"
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  <input
                    type="checkbox"
                    checked={formData.isPublished}
                    onChange={(e) => setFormData({...formData, isPublished: e.target.checked})}
                  />
                  Publish immediately
                </Label>
              </FormGroup>

              <FormActions>
                <ActionButton type="button" onClick={() => setShowModal(false)}>
                  <FiX />
                  Cancel
                </ActionButton>
                <ActionButton type="submit" variant="success">
                  <FiSave />
                  {editingBlog ? 'Update' : 'Create'} Blog
                </ActionButton>
              </FormActions>
            </Form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default AdminBlogsPage; 